/**
 * Who each remote participant is, for the media policy and both room layouts.
 *
 * Every endpoint announces its role at join as `malkaVisualRole`
 * (middleware.ts). These helpers read that announcement, and guess only for
 * peers that never announce (older mobile builds). The web layout
 * (VRSLayout.tsx), the native layout (VRSLayout.native.tsx) and the receive
 * policy (functions.ts) all use the same rules, so the pane a person is shown
 * in and the quality they are received at agree.
 *
 * Pure: no Redux, storage or platform imports, so native code and tests can
 * load it directly.
 */
/**
 * An announced or stored role as a visual-language role, or undefined when it
 * is not one. Admins and observers share one role: neither is on camera for
 * the call.
 *
 * @param {string} [role] - The announced or stored role.
 * @returns {VisualLanguageParticipantRole|undefined}
 */
export function normalizeVisualRole(role) {
    switch (role) {
        case 'admin':
        case 'superadmin':
        case 'observer':
            return 'admin-observer';
        case 'captioner':
        case 'client':
        case 'guest':
        case 'interpreter':
            return role;
        default:
            return undefined;
    }
}
/**
 * The participant a stored hint names (`vrs_target_client`: an id or a name),
 * matched case-insensitively and by substring.
 *
 * @param {P[]} participants - Who to look among.
 * @param {string} [hint] - The stored id or name.
 * @returns {P|undefined}
 */
export function findParticipantByHint(participants, hint) {
    if (!hint) {
        return undefined;
    }
    const normalizedHint = hint.trim().toLowerCase();
    return participants.find(participant => {
        const candidateNames = [
            participant.id,
            participant.name,
            participant.displayName
        ]
            .filter((value) => Boolean(value))
            .map(value => value.toLowerCase());
        return candidateNames.some(value => value === normalizedHint || value.includes(normalizedHint));
    });
}
/**
 * Roles in an interpreted room (VRS, VRI). An announced role always wins.
 * Only peers that never announced (older mobile builds) are guessed, and
 * only into a role nobody has announced: for a client, the first of them is
 * the interpreter; for an interpreter, the one `vrs_target_client` names (or
 * the first) is the client. Everyone else is a guest.
 *
 * The guess used to pick by position among everyone, announced or not, so
 * an announced guest who joined first used it up and an older interpreter
 * build was taken for a guest.
 *
 * @param {IVisualRoleParticipant[]} remoteParticipants - Visible remote participants, in join order.
 * @param {VisualLanguageParticipantRole} [localRole] - The local person's role.
 * @param {string} [targetClient] - The stored `vrs_target_client` hint.
 * @returns {Record<string, VisualLanguageParticipantRole>} Roles by participant id.
 */
export function inferRemoteRoles(remoteParticipants, localRole, targetClient) {
    const roleByParticipantId = {};
    remoteParticipants.forEach(participant => {
        const announcedRole = normalizeVisualRole(participant.malkaVisualRole);
        if (announcedRole) {
            roleByParticipantId[participant.id] = announcedRole;
        }
    });
    const announced = new Set(Object.values(roleByParticipantId));
    const unassigned = () => remoteParticipants.filter(participant => !roleByParticipantId[participant.id]);
    const guess = (role, pick) => {
        const participant = announced.has(role) ? undefined : pick(unassigned());
        if (participant) {
            roleByParticipantId[participant.id] = role;
        }
    };
    const firstOf = (candidates) => candidates[0];
    const namedClient = (candidates) => findParticipantByHint(candidates, targetClient) || candidates[0];
    if (localRole === 'interpreter') {
        guess('client', namedClient);
    }
    else if (localRole === 'client') {
        guess('interpreter', firstOf);
    }
    else {
        guess('interpreter', firstOf);
        guess('client', namedClient);
    }
    unassigned().forEach(participant => {
        roleByParticipantId[participant.id] = 'guest';
    });
    return roleByParticipantId;
}
/**
 * Roles in a deaf-to-deaf platform room (`p2p-`, `group-`). Everyone signs,
 * so no one is guessed to be the interpreter: a peer who announced nothing is
 * a signer ('client'). Only an interpreter called in through the in-call
 * interpreter button announces 'interpreter'.
 *
 * @param {IVisualRoleParticipant[]} remoteParticipants - Visible remote participants.
 * @returns {Record<string, VisualLanguageParticipantRole>} Roles by participant id.
 */
export function inferPlatformRoomRoles(remoteParticipants) {
    const roleByParticipantId = {};
    remoteParticipants.forEach(participant => {
        roleByParticipantId[participant.id] = normalizeVisualRole(participant.malkaVisualRole) || 'client';
    });
    return roleByParticipantId;
}
